import React, {useMemo} from 'react'
import {Flex, useSlotRecipe, Box} from '@chakra-ui/react3'
import PropTypes from 'prop-types'
import ModuleContainer from '../../components/module-container'
import Heading from '../../components/cms/heading'
import {campaignSlotRecipe} from './index.recipe'
import Copy from '../../components/cms/copy'
import Asset from '../../components/cms/asset'
import {hasValidAsset, hasValidString} from '../../utils/utils'
import CTA from '../../components/cms/cta'
import PriceButtonGroup from '../../components/cms/price-button-group'
import AspectRatio from '../../components/cms/aspect-ratio'
import Fade from '../../components/cms/fade'
import Parallax from '../../components/cms/parallax'
import ButtonGroup from '../../components/cms/button-group'

const Campaign = React.forwardRef(
    (
        {
            moduleId,
            moduleType,
            pageId,
            desktopImageAlignment = 'right',
            headingLevel,
            heading,
            copy,
            textAlignment,
            anchor,
            background,
            spacing,
            assetImageVideo,
            aspectRatio,
            textBackground,
            firstCta,
            secondCta,
            priceProduct
        },
        ref
    ) => {
        const recipe = useSlotRecipe({recipe: campaignSlotRecipe})
        const styles = useMemo(
            () =>
                recipe({
                    imageAlignment: desktopImageAlignment,
                    textBackground,
                    textAlignment
                }),
            [recipe, desktopImageAlignment, textBackground, textAlignment]
        )
        const hasFirstCtaLabel = hasValidString(firstCta?.label)
        const hasSecondCtaLabel = hasValidString(secondCta?.label)
        const hasPrice = priceProduct?.value != null

        const isImage = assetImageVideo.type === 'image'
        const assetElement = (
            <Asset asset={assetImageVideo} objectFit="cover" widths={{base: '100vw', s: '50vw'}} />
        )

        return (
            <ModuleContainer
                moduleId={moduleId}
                moduleType={moduleType}
                pageId={pageId}
                anchor={anchor}
                background={background}
                spacing={spacing}
                css={styles.moduleContainer}
                ref={ref}
            >
                <Flex css={styles.flexContainer}>
                    <Flex css={styles.flexItemText}>
                        <Flex css={styles.headingCopyContainer}>
                            <Fade>
                                <Heading {...heading} headingLevel={headingLevel} />
                            </Fade>

                            <Fade>
                                <Copy {...copy} alignment={styles.copy.alignment} />
                            </Fade>
                        </Flex>

                        <ButtonGroup
                            {...styles.buttonContainer}
                            orientation={hasPrice ? 'vertical' : 'horizontal'}
                            canWrap={true}
                        >
                            {hasFirstCtaLabel && !hasPrice && <CTA {...firstCta} />}

                            {hasFirstCtaLabel && hasPrice && (
                                <PriceButtonGroup
                                    price={priceProduct}
                                    cta={firstCta}
                                    layout="inline"
                                    variant="default"
                                />
                            )}

                            {hasSecondCtaLabel && <CTA {...secondCta} />}
                        </ButtonGroup>
                    </Flex>
                    {hasValidAsset(assetImageVideo) && (
                        /* Separate Box needed so flex sizing (flexItemImage) applies to the outer container,
                            not the AspectRatio which would break its intrinsic ratio calculation */
                        <Box css={styles.flexItemImage}>
                            <AspectRatio ratio={aspectRatio} pb="spacing-24">
                                {isImage ? <Parallax>{assetElement}</Parallax> : assetElement}
                            </AspectRatio>
                        </Box>
                    )}
                </Flex>
            </ModuleContainer>
        )
    }
)

if (process.env.NODE_ENV !== 'production') {
    Campaign.displayName = 'Campaign'
}

Campaign.propTypes = {
    headingLevel: PropTypes.number,
    heading: PropTypes.shape({
        variant: PropTypes.oneOf(['default', 'small', 'hidden']),
        subline: PropTypes.string,
        headline1: PropTypes.string,
        headline2: PropTypes.string
    }),
    background: PropTypes.oneOf(['default', 'subtle', 'strong', 'inverse']),
    anchor: PropTypes.string,
    moduleId: PropTypes.string,
    moduleType: PropTypes.string,
    pageId: PropTypes.string,
    spacing: PropTypes.oneOf(['default', 'reduced', 'min', 'min-top', 'min-bottom']),
    desktopImageAlignment: PropTypes.oneOf(['right', 'left']),
    aspectRatio: PropTypes.oneOf(['16/10', '4/3', '1/1']),
    textBackground: PropTypes.bool,
    firstCta: PropTypes.object,
    secondCta: PropTypes.object,
    copy: PropTypes.object,
    assetImageVideo: PropTypes.object,
    priceProduct: PropTypes.shape({
        currency: PropTypes.string,
        value: PropTypes.number,
        valueListPrice: PropTypes.number,
        label: PropTypes.string,
        hidePromotionSignals: PropTypes.bool
    }),
    textAlignment: PropTypes.oneOf(['top', 'center'])
}

export default Campaign
